// components/ChinaSiteMap/MapCanvas.jsx
// 复用 ems-ours-backup（作者 Lumineeeeee）的官方标准底图渲染：
// 官方中国 / 世界 SVG 底图 + 标定投影落点 + 省级区域悬停高亮 + 滚轮缩放 / 拖拽平移 + 站点悬浮卡片。
// 适配：去掉 i18n（改中文文案）与后端依赖，站点数据由调用方以本地演示数据传入。
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Button, Popover } from 'antd';

const base = process.env.PUBLIC_URL || '';
const assets = { china: `${base}/maps/china-standard.svg`, world: `${base}/maps/world-standard.svg` };

// 官方地图把直辖市叠加在相邻省份之上，抬升被覆盖省份时需减除同源路径。
const provinceCutouts = {
  130000: ['110000', '120000'],
  320000: ['310000'],
  330000: ['310000'],
};
const municipalityCodes = new Set(['110000', '120000', '310000']);
const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

function SiteMetrics({ site }) {
  const format = (value) => (Number.isFinite(value) ? new Intl.NumberFormat('zh-CN', { maximumFractionDigits: 1 }).format(value) : '—');
  return (
    <dl className="site-map-metrics">
      <div><dt>装机容量</dt><dd>{format(site.capacity)} <small>kWh</small></dd></div>
      <div><dt>装机功率</dt><dd>{format(site.power)} <small>kW</small></dd></div>
    </dl>
  );
}

function MapCanvas({ data, mode, selected, onSelect, sites, selectedSite, onSiteSelect, expanded = false }) {
  const id = useId().replace(/:/g, '');
  const svgRef = useRef(null);
  const dragRef = useRef(null);
  const dragMoved = useRef(false);
  const [viewport, setViewport] = useState({ scale: 1, x: 0, y: 0 });
  const [hovered, setHovered] = useState(null);
  const [failed, setFailed] = useState(false);
  const active = data.regions.find((region) => region.code === (hovered || selected));
  const municipalities = data.regions.filter((region) => municipalityCodes.has(region.code));
  const hitRegions = mode === 'china'
    ? [...data.regions.filter((region) => !municipalityCodes.has(region.code)), ...municipalities] : [];
  const toggle = (code) => onSelect(selected === code ? null : code);

  const zoomAt = useCallback((factor, u = 0.5, v = 0.5) => {
    setViewport((previous) => {
      const scale = clamp(previous.scale * factor, 1, 8);
      return {
        scale,
        x: clamp(previous.x + (u * data.width) / previous.scale - (u * data.width) / scale, 0, data.width - data.width / scale),
        y: clamp(previous.y + (v * data.height) / previous.scale - (v * data.height) / scale, 0, data.height - data.height / scale),
      };
    });
    setHovered(null);
  }, [data.width, data.height]);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return undefined;
    const onWheel = (event) => {
      event.preventDefault();
      if (dragRef.current) return;
      const rect = svg.getBoundingClientRect();
      const unit = Math.min(rect.width / data.width, rect.height / data.height);
      if (!unit) return;
      const u = clamp((event.clientX - rect.left - (rect.width - data.width * unit) / 2) / (data.width * unit), 0, 1);
      const v = clamp((event.clientY - rect.top - (rect.height - data.height * unit) / 2) / (data.height * unit), 0, 1);
      const delta = event.deltaY * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? rect.height : 1);
      zoomAt(Math.exp(-clamp(delta, -240, 240) * 0.002), u, v);
    };
    svg.addEventListener('wheel', onWheel, { passive: false });
    return () => svg.removeEventListener('wheel', onWheel);
  }, [data.width, data.height, zoomAt, failed]);

  const endDrag = (event) => {
    if (dragRef.current?.pointerId !== event.pointerId) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    dragRef.current = null;
  };

  if (failed) return <div className="china-map-error" role="alert">底图加载失败</div>;
  return (
    <div className={`china-map-stage${expanded ? ' china-map-stage-expanded' : ''}`}>
      <div className="site-map-zoom-toolbar">
        <span className="site-map-zoom-help">滚轮缩放 · 按住拖拽平移</span>
        <Button size="small" aria-label="缩小" disabled={viewport.scale <= 1} onClick={() => zoomAt(1 / 1.3)}>−</Button>
        <span className="site-map-zoom-level" aria-live="polite">{Math.round(viewport.scale * 100)}%</span>
        <Button size="small" aria-label="放大" disabled={viewport.scale >= 8} onClick={() => zoomAt(1.3)}>+</Button>
        <Button size="small" onClick={() => { setViewport({ scale: 1, x: 0, y: 0 }); setHovered(null); }}>重置</Button>
      </div>
      <svg
        ref={svgRef}
        viewBox={`${viewport.x} ${viewport.y} ${data.width / viewport.scale} ${data.height / viewport.scale}`}
        className="china-map-svg"
        data-zoomed={viewport.scale > 1}
        style={{ '--map-lift-offset': `${-7 / viewport.scale}px` }}
        aria-label={mode === 'china' ? '中国标准底图' : '世界标准底图'}
        onPointerDown={(event) => {
          dragMoved.current = false;
          if (event.button !== 0 || viewport.scale <= 1) return;
          const rect = event.currentTarget.getBoundingClientRect();
          const unit = Math.min(rect.width / data.width, rect.height / data.height);
          if (!unit) return;
          dragRef.current = { pointerId: event.pointerId, clientX: event.clientX, clientY: event.clientY, viewport, unit };
        }}
        onPointerMove={(event) => {
          const drag = dragRef.current;
          if (!drag || drag.pointerId !== event.pointerId) return;
          const dx = event.clientX - drag.clientX; const dy = event.clientY - drag.clientY;
          if (!dragMoved.current && Math.hypot(dx, dy) < 4) return;
          dragMoved.current = true;
          event.currentTarget.setPointerCapture(event.pointerId);
          const scale = drag.viewport.scale;
          setViewport({
            scale,
            x: clamp(drag.viewport.x - dx / drag.unit / scale, 0, data.width - data.width / scale),
            y: clamp(drag.viewport.y - dy / drag.unit / scale, 0, data.height - data.height / scale),
          });
          setHovered(null);
        }}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onPointerLeave={(event) => { if (!event.currentTarget.hasPointerCapture(event.pointerId)) endDrag(event); }}
        onClickCapture={(event) => {
          if (dragMoved.current) { event.preventDefault(); event.stopPropagation(); dragMoved.current = false; }
        }}
      >
        <defs>
          {/* 单幅整体处理，完整保留底图文字与几何 */}
          <filter id={`${id}-map-tone`} x="-5%" y="-5%" width="110%" height="110%" colorInterpolationFilters="sRGB">
            <feColorMatrix type="saturate" values="0" />
            <feComponentTransfer>
              <feFuncR type="table" tableValues="0.62 0.035" />
              <feFuncG type="table" tableValues="0.87 0.085" />
              <feFuncB type="table" tableValues="1 0.16" />
            </feComponentTransfer>
          </filter>
          {mode === 'china' && data.regions.filter((region) => region.paths.length).map((region) => (
            <clipPath key={region.code} id={`${id}-${region.code}`} clipPathUnits="userSpaceOnUse">
              {region.paths.map((path, index) => <path key={index} d={path.d} clipRule={path.fillRule} />)}
            </clipPath>
          ))}
          {mode === 'china' && Object.entries(provinceCutouts).map(([code, excluded]) => (
            <mask key={code} id={`${id}-${code}-cutouts`} maskUnits="userSpaceOnUse" maskContentUnits="userSpaceOnUse"
              x="0" y="0" width={data.width} height={data.height} style={{ maskType: 'luminance' }}>
              <rect width={data.width} height={data.height} fill="white" />
              {data.regions.filter((region) => excluded.includes(region.code)).flatMap((region) => region.paths.map((path, index) => (
                <path key={`${region.code}-${index}`} d={path.d} fillRule={path.fillRule} fill="black" />
              )))}
            </mask>
          ))}
        </defs>
        {/* 原始完整底图固定不动，仅交互副本进行抬升 */}
        <g className="site-map-artwork" filter={`url(#${id}-map-tone)`}>
          <image href={assets[mode]} width={data.width} height={data.height} onError={() => setFailed(true)} />
          {mode === 'china' && (
            <g className="china-map-municipality-borders" aria-hidden="true" pointerEvents="none">
              {municipalities.flatMap((region) => region.paths.map((path, index) => (
                <path key={`${region.code}-${index}`} d={path.d} fill="none" stroke="#18354e" strokeWidth="1.2" vectorEffect="non-scaling-stroke" />
              )))}
            </g>
          )}
          {hitRegions.map((region) => (
            <g
              key={region.code}
              className="china-map-hit"
              role="button"
              tabIndex={0}
              aria-label={region.name}
              aria-pressed={selected === region.code}
              onPointerEnter={() => { if (!dragRef.current) setHovered(region.code); }}
              onPointerLeave={() => setHovered(null)}
              onFocus={() => setHovered(region.code)}
              onBlur={() => setHovered(null)}
              onClick={() => toggle(region.code)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); toggle(region.code); }
                if (event.key === 'Escape') { setHovered(null); onSelect(null); }
              }}
            >
              <title>{region.name}</title>
              {region.paths.map((path, index) => <path key={index} d={path.d} fillRule={path.fillRule} />)}
              {region.point && <circle cx={region.point[0]} cy={region.point[1]} r="5" />}
            </g>
          ))}
          {mode === 'china' && !!active?.paths.length && (
            <g key={active.code} className="china-map-lift" aria-hidden="true">
              <g clipPath={`url(#${id}-${active.code})`} mask={provinceCutouts[active.code] ? `url(#${id}-${active.code}-cutouts)` : undefined}>
                <image href={assets[mode]} width={data.width} height={data.height} />
                {active.paths.map((path, index) => <path key={index} d={path.d} fillRule={path.fillRule} fill="#1677ff" fillOpacity=".12" />)}
                {municipalityCodes.has(active.code) && active.paths.map((path, index) => (
                  <path key={`border-${index}`} d={path.d} fill="none" stroke="#18354e" strokeWidth="1.2" vectorEffect="non-scaling-stroke" />
                ))}
              </g>
            </g>
          )}
          {mode === 'china' && active?.point && (
            <g className="china-map-lift" aria-hidden="true">
              <circle cx={active.point[0]} cy={active.point[1]} r="6" fill="#1677ff" fillOpacity=".25" stroke="#1677ff" />
            </g>
          )}
        </g>
        {sites.map((site) => {
          const point = mode === 'china' ? site.chinaPoint : site.worldPoint;
          if (!point) return null;
          const radius = mode === 'china' ? 7 : 4.5;
          return (
            <Popover
              key={site.id}
              color="#102c48"
              trigger={['hover', 'focus']}
              title={<span style={{ color: '#e6f5ff' }}>{site.name}</span>}
              content={<div className="site-map-hover-card"><SiteMetrics site={site} /></div>}
            >
              <g
                role="button"
                tabIndex={0}
                className={`site-map-marker ${site.hasActiveAlarm === true ? 'site-map-alarm' : site.hasActiveAlarm === false ? 'site-map-normal' : 'site-map-unknown'}${selectedSite?.id === site.id ? ' site-map-marker-selected' : ''}`}
                aria-label={site.name}
                aria-pressed={selectedSite?.id === site.id}
                onClick={() => onSiteSelect(site)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onSiteSelect(site); }
                  if (event.key === 'Escape') onSiteSelect(null);
                }}
                transform={`translate(${point[0]} ${point[1]}) scale(${1 / viewport.scale})`}
              >
                <title>{site.name} · {site.coordinates.lng}, {site.coordinates.lat}</title>
                <circle r={radius * 2} fill="transparent" />
                <circle className="site-map-marker-halo" r={radius * 1.65} />
                <circle className="site-map-marker-dot" r={radius} />
                <circle r={radius * 0.3} fill="#fff" pointerEvents="none" />
              </g>
            </Popover>
          );
        })}
      </svg>
      {(mode === 'china' || active) && (
        <div className="china-map-selection" aria-live="polite">
          {active ? active.name : '悬停查看省级区域 · 滚轮缩放地图'}
        </div>
      )}
    </div>
  );
}

export default MapCanvas;
